import { useState } from 'react'
import {
  Typography,
  Paper,
  Box,
  Alert,
  AlertTitle,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider
} from '@mui/material'
import ExportButton from '../../components/ExportButton'
import ProgressBar from '../../components/ProgressBar'
import ExportFilters from '../../components/ExportFilters'
import { usePageTitle } from '../../hooks/usePageTitle'
import { useBulkExport } from '../../hooks/useBulkExport'

type Region = 'US' | 'ANZ'

function LightspeedBulkSales() {
  usePageTitle('Lightspeed', 'Bulk Sales Export')

  const [region, setRegion] = useState<Region>('US')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [localError, setLocalError] = useState<string | null>(null)

  // Use bulk export hook
  const {
    loading,
    error: exportError,
    success: exportSuccess,
    warning: exportWarning,
    progressMessage,
    currentCount,
    totalCount,
    fpErrors,
    startExport
  } = useBulkExport({
    exportType: 'ls_sales'
  })

  // Combine errors
  const error = exportError || localError
  const warning = exportWarning

  const handleExport = async () => {
    setLocalError(null)

    // Validate date range
    if (dateFrom && dateTo) {
      if (dateFrom > dateTo) {
        setLocalError(`Invalid date range: 'From' date (${dateFrom}) is after 'To' date (${dateTo}). Please swap the dates.`)
        return
      }
    }

    // Start the bulk export
    await startExport({
      region,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined
    })
  }

  // Calculate progress percentage
  const progressPercentage = totalCount > 0 ? Math.round((currentCount / totalCount) * 100) : null

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          This tool exports sales data for <strong>ALL franchise partners</strong> in the selected region.
          Use this for regional reporting and analysis across multiple FPs.
        </Alert>

        {/* Region Selection */}
        <Paper sx={{ p: 3 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
              Select Region
            </FormLabel>
            <RadioGroup
              row
              value={region}
              onChange={(e) => setRegion(e.target.value as Region)}
            >
              <FormControlLabel
                value="US"
                control={<Radio sx={{ color: 'info.main', '&.Mui-checked': { color: 'info.main' } }} />}
                label={
                  <Box>
                    <Typography variant="body1">United States</Typography>
                    <Typography variant="caption" color="text.secondary">
                      43 franchise partners
                    </Typography>
                  </Box>
                }
                sx={{ mr: 6 }}
              />
              <FormControlLabel
                value="ANZ"
                control={<Radio sx={{ color: 'info.main', '&.Mui-checked': { color: 'info.main' } }} />}
                label={
                  <Box>
                    <Typography variant="body1">Australia & New Zealand</Typography>
                    <Typography variant="caption" color="text.secondary">
                      115+ franchise partners
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Paper>

        {/* Date Range Filter */}
        <ExportFilters
          title="Date Range (Required)"
          filters={[
            {
              id: 'daterange',
              label: 'Date Range',
              type: 'daterange',
              value: { from: dateFrom, to: dateTo },
              onChange: (value) => {
                setDateFrom(value.from)
                setDateTo(value.to)
              }
            }
          ]}
        />

        {/* Export Options Info */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Export Details
          </Typography>

          <Typography variant="body2" color="text.secondary" paragraph>
            The export will include the following fields for each sale:
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {[
              'FP Code', 'Company', 'Outlet ID', 'Invoice #', 'Customer',
              'Created', 'Due', 'Paid Date', 'Subtotal', 'Tax',
              'Surcharge', 'Total', 'Paid Amt', 'Status'
            ].map((field) => (
              <Typography
                key={field}
                variant="caption"
                sx={{
                  bgcolor: 'grey.100',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontFamily: 'monospace'
                }}
              >
                {field}
              </Typography>
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary">
            Output format: <strong>CSV</strong> (optimized for large datasets)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Processing: Up to 5 FPs processed in parallel
          </Typography>
        </Paper>

        {/* Date Range Required Info */}
        {(!dateFrom || !dateTo) && !loading && (
          <Alert severity="info">
            Please select a date range to export. A date range is required for bulk exports.
          </Alert>
        )}

        {/* Error/Warning/Success Messages */}
        {error && (
          <Alert severity="error" onClose={() => setLocalError(null)}>
            {error}
          </Alert>
        )}

        {warning && (
          <Alert severity="warning">
            {warning}
          </Alert>
        )}

        {exportSuccess && (
          <Alert severity="success">
            {exportSuccess}
          </Alert>
        )}

        {/* FP Errors after export */}
        {fpErrors && fpErrors.length > 0 && (
          <Alert severity="warning">
            <AlertTitle>{fpErrors.length} FP(s) had errors:</AlertTitle>
            {fpErrors.map((e) => (
              <Typography key={e.fp_code} variant="body2">
                <strong>{e.fp_code}</strong>: {e.error}
              </Typography>
            ))}
          </Alert>
        )}

        {/* Progress Display */}
        {loading && (
          <Paper sx={{ p: 3 }}>
            <ProgressBar
              value={progressPercentage || 0}
              indeterminate={progressPercentage === null}
              message={progressMessage || `Processing ${region} franchise partners...`}
            />
            {currentCount > 0 && totalCount > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                {currentCount} of {totalCount} FPs processed
              </Typography>
            )}
          </Paper>
        )}

        {/* Export Button */}
        <ExportButton
          onClick={handleExport}
          loading={loading}
          disabled={loading || !dateFrom || !dateTo}
          text={`Export ${region} Sales`}
        />
      </Box>
    </Box>
  )
}

export default LightspeedBulkSales
